import React, { useState, useEffect } from 'react';
import { Calendar, Edit, Trash2, RotateCw } from 'lucide-react';
import { Table, Space, Button } from 'antd';
import PermissionDeniedModal from '../../../components/Modals/PermissionDeniedModal';

const ScheduledJobs = ({canEdit, canDelete}) => {
  const [scheduledTasks, setScheduledTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [deniedAction, setDeniedAction] = useState('');

  useEffect(() => {
    fetchScheduledTasks();
  }, []);

  const fetchScheduledTasks = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`https://cloudify-prod.wizr.in/scheduled-tasks/batch-jobs`);
      const data = await response.json();
      setScheduledTasks(data.tasks.map((task, index) => ({
        ...task,
        key: task.ruleArn || index,
        number: index + 1
      })));
    } catch (error) {
      console.error('Failed to fetch scheduled tasks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (task) => {
    if (!canEdit) {
      setDeniedAction('edit scheduled jobs');
      setPermissionDenied(true);
      return;
    }
  };

  const handleDelete = async (ruleName) => {
    if (!canDelete) {
      setDeniedAction('delete scheduled jobs');
      setPermissionDenied(true);
      return;
    }

    try {
      await fetch(`https://cloudify-prod.wizr.in/scheduled-tasks/${ruleName}`, {
        method: 'DELETE'
      });
      fetchScheduledTasks();
    } catch (error) {
      console.error('Failed to delete scheduled task:', error);
    }
  };

  const columns = [
    {
      title: 'Task #',
      dataIndex: 'number',
      key: 'number',
    },
    {
      title: 'Rule Name',
      dataIndex: 'ruleName',
      key: 'ruleName',
    },
    {
      title: 'Schedule Expression',
      dataIndex: 'scheduleExpression',
      key: 'scheduleExpression',
      render: (schedule) => (
        <div className="flex items-center text-gray-700">
          <Calendar className="h-4 w-4 mr-2 text-gray-500" />
          {schedule}
        </div>
      ),
    },
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      render: (state) => {
        const statusConfig = {
          ENABLED: {
            bgColor: 'bg-green-100',
            textColor: 'text-green-800',
            borderColor: 'border-green-200',
            icon: '✓'
          },
          DISABLED: {
            bgColor: 'bg-yellow-100',
            textColor: 'text-yellow-800',
            borderColor: 'border-yellow-200',
            icon: '⏸'
          },
          default: {
            bgColor: 'bg-gray-100',
            textColor: 'text-gray-800',
            borderColor: 'border-gray-200',
            icon: '•'
          }
        };
        
        const config = statusConfig[state] || statusConfig.default;
        
        return (
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor} border ${config.borderColor} flex items-center justify-center w-24`}>
            <span className="mr-1">{config.icon}</span> {state}
          </span>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, task) => (
        <Space>
          <button
            onClick={() => handleEdit(task)}
            className="p-1.5 text-gray-400 hover:text-indigo-600 rounded-full hover:bg-indigo-50 transition-colors duration-200"
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleDelete(task.ruleName)}
            className="p-1.5 text-gray-400 hover:text-red-600 rounded-full hover:bg-red-50 transition-colors duration-200"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </Space>
      ),
    },
  ];

  return (
    <div className="space-y-6 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
      <div className="flex justify-between items-center pb-4 border-b border-gray-100">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold text-gray-900">Scheduled Tasks</h2>
          <p className="text-sm text-gray-500">View and manage your scheduled ECS tasks</p>
        </div>
        <div className="space-x-2">
          <Button
            onClick={fetchScheduledTasks}
            loading={isLoading}
            icon={<RotateCw className="h-4 w-4" />}
          >
            Refresh
          </Button>
        </div>
      </div>
      
      <div className="rounded-lg overflow-hidden border border-gray-200">
        <Table
          columns={columns}
          dataSource={scheduledTasks}
          loading={isLoading}
          rowKey="key"
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} scheduled tasks`,
          }}
        />
      </div>
      
      <PermissionDeniedModal
        visible={permissionDenied}
        onClose={() => setPermissionDenied(false)}
        message={`You don't have permission to ${deniedAction}. Please contact your administrator.`}
      />
    </div>
  );
};

export default ScheduledJobs;

